import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useInstalarPermanente } from '../../lib/pwaInstall';

/**
 * Ícone PERMANENTE de instalação no header do app do comprador (modelo AMA
 * Flow): download discreto no canto superior direito, ao lado do Sair. SEMPRE
 * visível no mobile enquanto o app não está instalado — independe do banner e
 * do beforeinstallprompt. Some em standalone; no desktop nunca aparece.
 *
 * Ao tocar, SEMPRE abre um sheet (feedback garantido). O sheet é renderizado
 * num portal no <body> para não ser cortado por nenhum ancestral. Dentro:
 *  - Android com instalador nativo → botão "Instalar app" (dispara o prompt)
 *    MAIS o passo a passo do menu como garantia (o prompt nativo às vezes falha);
 *  - Android sem o evento → passo a passo do menu (⋮ → Instalar app);
 *  - iOS → passo a passo Compartilhar → Adicionar à Tela de Início.
 */
export function InstalarIconeHeader({
  orgNome,
  corMarca,
}: {
  orgNome?: string | null;
  corMarca?: string | null;
}) {
  const { visivel, plataforma, instalarNativo } = useInstalarPermanente();
  const [aberto, setAberto] = useState(false);
  if (!visivel) return null;

  const nome = orgNome?.trim() || 'incorporadora';
  const acento = corMarca && /^#[0-9a-fA-F]{6}$/.test(corMarca) ? corMarca : 'var(--brand)';

  const sheet = aberto && typeof document !== 'undefined'
    ? createPortal(
        <div
          className="fixed inset-0 z-[999] flex items-end justify-center bg-black/40"
          data-testid="comprador-instalar-sheet"
          role="dialog"
          aria-modal="true"
          onClick={() => setAberto(false)}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-surface p-6"
            style={{ boxShadow: 'var(--shadow-sm)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start gap-3">
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                style={{ background: 'var(--brand-soft, var(--accent-soft))', color: acento }}
                aria-hidden="true"
              >
                <IconeBaixar tamanho={22} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-ink">Instalar o app da {nome}</div>
                <div className="mt-0.5 text-xs text-ink-3">Acesso rápido pela tela inicial, sem passar pelo navegador.</div>
              </div>
              <button
                type="button"
                onClick={() => setAberto(false)}
                aria-label="Fechar"
                data-testid="comprador-instalar-sheet-fechar"
                className="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 text-ink-3 hover:bg-bg-warm hover:text-ink"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Android com instalador nativo: botão direto (com o passo a passo
                logo abaixo como garantia). */}
            {plataforma === 'android' && instalarNativo && (
              <button
                type="button"
                onClick={() => void instalarNativo()}
                data-testid="comprador-instalar-nativo"
                className="mb-4 w-full rounded-xl px-4 py-3 text-sm font-semibold text-white"
                style={{ background: acento }}
              >
                Instalar app
              </button>
            )}

            {plataforma === 'ios' ? (
              <ol className="space-y-3" data-testid="instrucao-ios">
                <PassoInstrucao numero="1" acento={acento} icone={<IconeCompartilhar />}>
                  Toque em <strong>Compartilhar</strong> na barra do navegador.
                </PassoInstrucao>
                <PassoInstrucao numero="2" acento={acento} icone={<IconeAdicionar />}>
                  Escolha <strong>Adicionar à Tela de Início</strong>.
                </PassoInstrucao>
              </ol>
            ) : (
              <ol className="space-y-3" data-testid="instrucao-android">
                <PassoInstrucao numero="1" acento={acento} icone={<IconeMenu />}>
                  Abra o menu <strong>(⋮)</strong> do navegador, no canto superior.
                </PassoInstrucao>
                <PassoInstrucao numero="2" acento={acento} icone={<IconeBaixar />}>
                  Toque em <strong>Instalar app</strong> (ou <strong>Adicionar à tela inicial</strong>).
                </PassoInstrucao>
              </ol>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label={`Instalar o app da ${nome}`}
        data-testid="comprador-instalar-icone"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-surface hover:text-ink"
      >
        <IconeBaixar tamanho={18} />
      </button>
      {sheet}
    </>
  );
}

function PassoInstrucao({
  numero,
  acento,
  icone,
  children,
}: {
  numero: string;
  acento: string;
  icone: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 rounded-xl border border-line px-3.5 py-3">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
        style={{ background: acento }}
      >
        {numero}
      </span>
      <span className="shrink-0 text-ink-2" aria-hidden="true">
        {icone}
      </span>
      <span className="min-w-0 flex-1 text-sm text-ink-2">{children}</span>
    </li>
  );
}

function IconeBaixar({ tamanho = 20 }: { tamanho?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} fill="none" aria-hidden="true">
      <path d="M12 3v11m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 17v2a2 2 0 002 2h10a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconeCompartilhar() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path d="M12 3v12m0-12l-3.5 3.5M12 3l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 11H5a2 2 0 00-2 2v6a2 2 0 002 2h14a2 2 0 002-2v-6a2 2 0 00-2-2h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconeAdicionar() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8.5v7M8.5 12h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconeMenu() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" />
    </svg>
  );
}
